import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { LayoutContext } from "@/lib/models";
import { siteUrlBuilder } from "@/lib/url-builder";
import { ClipboardPlus, Grid, List, Map, Trash2,} from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useOutletContext, useParams } from "react-router";
import Header from "./Header";

interface CardGridViewProps<T extends { id: string; name: string }> {
  items: T[];
  renderContentItem: (item: T) => React.ReactNode;
  tableHeaders: { id: string; child: React.ReactNode }[];
  tableContentItem: (item: T) => React.ReactNode[];
  createPageUrl?: string;
  editPageUrl: (parent: string, id: string) => string;
  onDeleteItem?: (item: T) => Promise<void>;
  title: string;
}

const CardGridView = <T extends { id: string; name: string }>({
  items,
  renderContentItem,
  tableHeaders,
  tableContentItem,
  createPageUrl,
  editPageUrl,
  onDeleteItem,
  title,
}: CardGridViewProps<T>) => {
  const params = useParams();
  const navigate = useNavigate();
  const { setPageName } = useOutletContext<LayoutContext>();
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");

  useEffect(() => {
    setPageName("Tasks List"); // Set the page name when the component mounts
    return () => setPageName("Tasks List"); // Optional: Reset on unmount
  }, [setPageName]);

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <Link to={siteUrlBuilder(params.siteId!)}>
            <Map className="group-hover:text-blue-600 transition-colors duration-200 cursor-pointer" />
          </Link>
          <Header headerTitle={title} />
          <div className="flex items-start gap-2">
            <Button
              variant={viewMode === "grid" ? "default" : "outline"}
              size="sm"
              onClick={() => setViewMode("grid")}
            >
              <Grid className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === "list" ? "default" : "outline"}
              size="sm"
              onClick={() => setViewMode("list")}
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
        </div>
        {createPageUrl && (
          <>
            <Link
              to={createPageUrl}
              className="group focus:outline-none bg-white"
            >
              <ClipboardPlus className="h-6 w-6 text-gray-500 group-hover:text-blue-600 transition-colors duration-200 cursor-pointer" />
            </Link>
          </>
        )}
      </div>
      <div className="overflow-x-auto overflow-y-auto pb-4">
        {viewMode === "grid" ? (
          <div
            className={
              viewMode === "grid"
                ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
                : "space-y-4"
            }
          >
            {items.map((item) => (
              <Card
                key={item.id}
                className={`cursor-pointer transition-all hover:shadow-lg`}
                onClick={() => navigate(editPageUrl(params.siteId!, item.id))}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-lg font-semibold text-gray-900 mb-1">
                      {item.name}
                    </CardTitle>
                    {onDeleteItem && (
                    <button 
                      className="hover:text-red-600 transition-colors duration-200 cursor-pointer"
                      onClick={async (e) => {
                            e.stopPropagation();
                            await onDeleteItem(item as unknown as T);
                          }}>
                      <Trash2 className="h-4 w-4 mr-2" />
                    </button>
                    )}
                    {/* <DropdownMenu>
                      <DropdownMenuTrigger
                        asChild
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Button variant="ghost" size="sm">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(editPageUrl(params.siteId!, item.id));
                          }}
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          Open
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={async (e) => {
                            e.stopPropagation();
                            await onDeleteItem(item as unknown as T);
                          }}
                          className="text-red-600"
                        >
                          
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu> */}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-1">{renderContentItem(item)}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <table className="min-w-full bg-white border border-gray-200 rounded-lg shadow-md">
            <thead>
              <tr className="bg-gray-50">
                {tableHeaders.map((header) => (
                  <th
                    key={header.id}
                    className="py-2 px-4 text-left text-sm font-semibold text-gray-700"
                  >
                    {header.child}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr>
                  {tableContentItem(item).map((item) => (
                    <td className="py-2 px-4 text-sm text-gray-600">{item}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
};

export default CardGridView;
